import { createConsumer } from '@rails/actioncable';

const PRESENCE_INTERVAL = 20000;
const RECONNECT_INTERVAL = 1000;
const TAB_ID_STORAGE_KEY = 'cw_dashboard_tab_id';

const generateTabId = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const getOrCreateTabId = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }

  try {
    const existingTabId = window.sessionStorage.getItem(TAB_ID_STORAGE_KEY);
    if (existingTabId) {
      return existingTabId;
    }

    const tabId = generateTabId();
    window.sessionStorage.setItem(TAB_ID_STORAGE_KEY, tabId);
    return tabId;
  } catch (_error) {
    return null;
  }
};

class BaseActionCableConnector {
  static isDisconnected = false;

  constructor(app, pubsubToken, websocketHost = '') {
    const websocketURL = websocketHost ? `${websocketHost}/cable` : undefined;
    const currentUserId = app.$store.getters.getCurrentUserID;
    const channelParams = {
      channel: 'RoomChannel',
      pubsub_token: pubsubToken,
      account_id: app.$store.getters.getCurrentAccountId,
      user_id: currentUserId,
    };
    const tabId = currentUserId ? getOrCreateTabId() : null;

    if (tabId) {
      channelParams.tab_id = tabId;
    }

    this.consumer = createConsumer(websocketURL);
    this.subscription = this.consumer.subscriptions.create(channelParams, {
      updatePresence() {
        this.perform('update_presence');
      },
      received: this.onReceived,
      disconnected: () => {
        BaseActionCableConnector.isDisconnected = true;
        this.onDisconnected();
        this.initReconnectTimer();
      },
    });
    this.app = app;
    this.events = {};
    this.reconnectTimer = null;
    this.isAValidEvent = () => true;
    this.triggerPresenceInterval = () => {
      setTimeout(() => {
        this.subscription.updatePresence();
        this.triggerPresenceInterval();
      }, PRESENCE_INTERVAL);
    };
    this.triggerPresenceInterval();
  }

  checkConnection() {
    const isConnectionActive = this.consumer.connection.isOpen();
    const isReconnected =
      BaseActionCableConnector.isDisconnected && isConnectionActive;
    if (isReconnected) {
      this.clearReconnectTimer();
      this.onReconnect();
      BaseActionCableConnector.isDisconnected = false;
    } else {
      this.initReconnectTimer();
    }
  }

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  initReconnectTimer = () => {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, RECONNECT_INTERVAL);
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  disconnect() {
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isAValidEvent(data)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
}

export default BaseActionCableConnector;
